import React, { useMemo, useCallback, useEffect, type KeyboardEvent, useState } from 'react';
import debounce from 'lodash/debounce';
import {
	type OptionType,
	type OptionsType,
	AsyncSelect,
	type StylesConfig,
	type SelectProps,
} from '@atlaskit/select';
import { useIntl } from '@atlassian/jira-intl';
import { useFlagsService } from '@atlassian/jira-flags';
import type { Group } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/insight-roles.tsx';
import { selectInAModalStyleFixProps } from '@atlassian/jira-servicedesk-insight-shared-ui/src/common/constants/index.tsx';
import { useGroupsSearch } from '../services/index.tsx';
import messages from './messages.tsx';
import type { GroupsPickerOption } from './types.tsx';
import { transformGroupToSelectOption, transformOptionToGroup } from './utils.tsx';

type Props = {
	isMulti: boolean;
	value: Group[];
	// used in the context of an insight inline editable attribute
	validationState?: 'default' | 'error';
	onChange: (arg1: Group[]) => void;
	defaultMenuIsOpen?: boolean;
	classNamePrefix?: string;
	autoFocus?: boolean;
	onKeyDown?: (event: KeyboardEvent) => void;
	// The below passed in types have no effect but are required
	spacing?: SelectProps<unknown, false>['spacing'];
	stylesOverride?: StylesConfig;
	isClearable?: boolean;
	shouldInputRenderValue?: boolean;
	isDisabled?: boolean;
	ariaDescribedBy?: string | undefined;
};

export const GroupsPicker = ({
	onKeyDown,
	isClearable,
	shouldInputRenderValue,
	stylesOverride,
	...props
}: Props) => {
	const { showFlag } = useFlagsService();
	const [{ error }, { searchGroups }] = useGroupsSearch();
	const { formatMessage } = useIntl();

	useEffect(() => {
		if (error != null) {
			showFlag({
				type: 'error',
				title: messages.errorFlagTitle,
				description: messages.errorFlagDescription,
				messageId: 'servicedesk-insight-groups-picker.ui.showFlag.error',
				messageType: 'transactional',
			});
		}
	}, [error, showFlag]);

	const loadOptions = useCallback(
		(query: string, cb: (arg1: GroupsPickerOption[]) => void) => {
			searchGroups(query).then((groups) => {
				const results = groups?.map(transformGroupToSelectOption) ?? [];
				cb(results);
			});
		},
		[searchGroups],
	);

	const debouncedLoadOptions = useMemo(() => debounce(loadOptions, 300), [loadOptions]);
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

	return (
		<AsyncSelect
			data-testid="servicedesk-insight-groups-picker.ui.select"
			classNamePrefix="cmdb-groups-picker"
			{...selectInAModalStyleFixProps}
			{...props}
			// @ts-expect-error - TS2322 - Type 'boolean' is not assignable to type 'false'.
			isMulti={props.isMulti}
			defaultOptions
			menuPlacement="auto"
			loadOptions={debouncedLoadOptions}
			value={props.value.map(transformGroupToSelectOption)}
			validationState={props.validationState}
			onChange={(groupOptions: OptionType | OptionsType<OptionType> | null) => {
				// group options can be null when clearing a value/s from the group picker
				if (groupOptions === null) {
					props.onChange([]);
				} else {
					const value = props.isMulti ? groupOptions : [groupOptions];
					props.onChange(value.map(transformOptionToGroup));
				}
			}}
			isClearable={isClearable}
			controlShouldRenderValue={shouldInputRenderValue}
			autoFocus={props.autoFocus}
			onKeyDown={(e: KeyboardEvent) => {
				if (!isMenuOpen && onKeyDown) {
					onKeyDown(e);
				}
			}}
			onMenuOpen={() => setIsMenuOpen(true)}
			onMenuClose={() => setIsMenuOpen(false)}
			spacing={props.spacing}
			label={formatMessage(messages.selectGroup)}
			{...(stylesOverride ? { styles: stylesOverride } : {})}
			descriptionId={props.ariaDescribedBy}
		/>
	);
};

GroupsPicker.defaultProps = {
	isMulti: true,
	validationState: 'default',
	autoFocus: false,
	shouldInputRenderValue: true,
	isClearable: true,
};
